// src/contexts/AuthContext.tsx — app-level auth context with refresh token logic
import * as React from "react";
import {
  type AuthUser,
  loginApi,
  registerApi,
  refreshTokenApi,
  logoutApi,
  getMeApi,
} from "../lib/authApi";
import { setTokenAccessor, setRefreshFunction } from "../lib/apiClient";
import { moduleViewPermission } from "../lib/permissions";
import { storeCachedAvatar, syncCachedAvatar } from "../lib/avatar";
import { isSessionRejected, readStoredSession, refreshStoredSession } from '../lib/sessionRefresh.mjs';

// ── Types ───────────────────────────────────────────────────────────
type AuthContextValue = {
  user: AuthUser | null;
  accessToken: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLead: boolean;
  role: "admin" | "lead" | "user" | null;
  permissions: string[];
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  hasAccess: (module: string) => boolean;
  isPendingApproval: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    setupToken?: string
  ) => Promise<{ pendingApproval: boolean }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<string | null>;
  updateUser: (updates: Partial<AuthUser>) => void;
};

const Ctx = React.createContext<AuthContextValue | undefined>(undefined);

// ── Helpers ─────────────────────────────────────────────────────────
const LS_REFRESH = "refresh_token";
const LS_ACCESS = "access_token";
const LS_USER_CACHE = "auth_user_cache";

function parseJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// ── Provider ────────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(() => {
    try {
      const cached = localStorage.getItem(LS_USER_CACHE);
      return cached ? syncCachedAvatar(JSON.parse(cached)) : null;
    } catch {
      return null;
    }
  });
  const [accessToken, setAccessToken] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const refreshTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTokenRef = React.useRef<string | null>(null);
  // Guard to prevent concurrent refresh calls
  const refreshPromiseRef = React.useRef<Promise<string | null> | null>(null);

  // Keep refreshTokenRef in sync with localStorage
  React.useEffect(() => {
    refreshTokenRef.current = localStorage.getItem(LS_REFRESH);
  }, []);

  // Persist user cache to localStorage
  const setUserAndCache = React.useCallback((u: AuthUser | null) => {
    const next = syncCachedAvatar(u);
    setUser(next);
    try {
      if (next) {
        localStorage.setItem(LS_USER_CACHE, JSON.stringify(next));
      } else {
        localStorage.removeItem(LS_USER_CACHE);
      }
    } catch {}
  }, []);

  // Schedule auto-refresh timer
  const scheduleRefresh = React.useCallback(
    (token: string) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      const exp = parseJwtExp(token);
      if (!exp) return;
      // Refresh 60 seconds before expiry
      const delay = Math.max(exp - Date.now() - 60_000, 5_000);
      refreshTimerRef.current = setTimeout(() => {
        void refreshSession().catch(() => {});
      }, delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Core refresh logic
  const refreshSession = React.useCallback(async (): Promise<string | null> => {
    // Prevent concurrent refreshes
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const rt = localStorage.getItem(LS_REFRESH);
    if (!rt) return null;

    const promise = (async () => {
      try {
        const data = await refreshStoredSession({ storage: localStorage, locks: navigator.locks, request: refreshTokenApi });
        if (!data) return null;
        // Store new tokens
        setAccessToken(data.accessToken);
        refreshTokenRef.current = data.refreshToken;
        setUserAndCache(data.user);
        scheduleRefresh(data.accessToken);
        return data.accessToken;
      } catch (error) {
        if (!isSessionRejected(error)) {
          // Temporary failures must not destroy a recoverable session.
          if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = setTimeout(() => { void refreshSession().catch(() => {}); }, 30_000);
          throw error;
        }
        setAccessToken(null);
        refreshTokenRef.current = null;
        setUserAndCache(null);
        localStorage.removeItem(LS_ACCESS);
        localStorage.removeItem(LS_REFRESH);
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, [setUserAndCache, scheduleRefresh]);

  // Wire up apiClient token accessor and refresh function
  React.useEffect(() => {
    setTokenAccessor(() => localStorage.getItem(LS_ACCESS));
    setRefreshFunction(refreshSession);
  }, [accessToken, refreshSession]);

  React.useEffect(() => {
    if (!accessToken) return;
    let active = true, pending = false;
    const sync = async () => {
      if (pending || document.hidden) return;
      pending = true;
      try { const result = await getMeApi(accessToken); if (active) setUserAndCache(result.user); }
      catch { /* Request authorization remains server enforced; regular refresh handles expiry. */ }
      finally { pending = false; }
    };
    const timer = setInterval(sync, 60_000);
    window.addEventListener('focus', sync);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', sync); };
  }, [accessToken, setUserAndCache]);

  // Initialize: try to refresh on mount
  React.useEffect(() => {
    const init = async () => {
      const rt = localStorage.getItem(LS_REFRESH);
      if (rt) {
        refreshTokenRef.current = rt;
        try { await refreshSession(); }
        catch {
          const saved = readStoredSession(localStorage);
          if (saved) {
            setAccessToken(saved.accessToken);
            setUserAndCache(saved.user);
          }
        }
      } else {
        // No refresh token — clear cached user
        setUserAndCache(null);
      }
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Listen for auth:logout event (from apiClient on unrecoverable 401)
  React.useEffect(() => {
    const handler = () => {
      setAccessToken(null);
      refreshTokenRef.current = null;
      setUserAndCache(null);
      localStorage.removeItem(LS_ACCESS);
      localStorage.removeItem(LS_REFRESH);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
    window.addEventListener("auth:logout", handler);
    return () => window.removeEventListener("auth:logout", handler);
  }, [setUserAndCache]);

  React.useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.storageArea !== localStorage || (event.key !== LS_REFRESH && event.key !== null)) return;
      const saved = readStoredSession(localStorage);
      refreshTokenRef.current = saved?.refreshToken || null;
      setAccessToken(saved?.accessToken || null);
      setUserAndCache(saved?.user || null);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (saved) scheduleRefresh(saved.accessToken);
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [setUserAndCache, scheduleRefresh]);

  // ── Actions ─────────────────────────────────────────────────────
  const login = React.useCallback(
    async (email: string, password: string) => {
      const data = await loginApi(email, password);
      setAccessToken(data.accessToken);
      localStorage.setItem(LS_ACCESS, data.accessToken);
      refreshTokenRef.current = data.refreshToken;
      setUserAndCache(data.user);
      localStorage.setItem(LS_REFRESH, data.refreshToken);
      scheduleRefresh(data.accessToken);
    },
    [setUserAndCache, scheduleRefresh]
  );

  const register = React.useCallback(
    async (
      email: string,
      password: string,
      name: string,
      setupToken?: string
    ): Promise<{ pendingApproval: boolean }> => {
      const data = await registerApi(email, password, name, setupToken);
      if ("accessToken" in data && data.accessToken) {
        // First user — auto-approved admin
        setAccessToken(data.accessToken);
        localStorage.setItem(LS_ACCESS, data.accessToken);
        refreshTokenRef.current = data.refreshToken;
        setUserAndCache(data.user);
        localStorage.setItem(LS_REFRESH, data.refreshToken);
        scheduleRefresh(data.accessToken);
        return { pendingApproval: false };
      }
      return { pendingApproval: true };
    },
    [setUserAndCache, scheduleRefresh]
  );

  const logout = React.useCallback(async () => {
    try {
      if (accessToken) {
        await logoutApi(accessToken).catch(() => {});
      }
    } finally {
      setAccessToken(null);
      refreshTokenRef.current = null;
      setUserAndCache(null);
      localStorage.removeItem(LS_ACCESS);
      localStorage.removeItem(LS_REFRESH);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
  }, [accessToken, setUserAndCache]);

  const updateUser = React.useCallback(
    (updates: Partial<AuthUser>) => {
      setUser((prev) => {
        if (!prev) return prev;
        if (Object.prototype.hasOwnProperty.call(updates, "avatar_url")) {
          storeCachedAvatar(prev, updates.avatar_url ?? null);
        }
        const next = syncCachedAvatar({ ...prev, ...updates });
        try {
          localStorage.setItem(LS_USER_CACHE, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    []
  );

  // ── Derived state ─────────────────────────────────────────────
  const isAuthenticated = !!user && !!accessToken;
  const isAdmin = user?.role === "admin";
  const isLead = user?.role === "lead";
  const role = (user?.role as "admin" | "lead" | "user") ?? null;
  const isPendingApproval = !!user && user.approved === 0 && user.blocked === 0;
  const permissions = React.useMemo(() => {
    const list = Array.isArray(user?.permissions) ? user.permissions : [];
    return user?.role === "admin" ? Array.from(new Set(["*", ...list])) : list;
  }, [user]);

  const can = React.useCallback(
    (permission: string) => {
      if (!user) return false;
      if (user.role === "admin" || permissions.includes("*")) return true;
      return permissions.includes(permission);
    },
    [permissions, user],
  );

  const canAny = React.useCallback(
    (items: string[]) => items.some((permission) => can(permission)),
    [can],
  );

  const hasAccess = React.useCallback(
    (mod: string) => {
      if (!user) return false;
      if (mod === 'access-requests') return true;
      const permission = moduleViewPermission(mod);
      return Array.isArray(permission) ? canAny(permission) : can(permission);
    },
    [can, canAny, user],
  );

  const value: AuthContextValue = React.useMemo(
    () => ({
      user,
      accessToken,
      loading,
      isAuthenticated,
      isAdmin,
      isLead,
      role,
      permissions,
      can,
      canAny,
      hasAccess,
      isPendingApproval,
      login,
      register,
      logout,
      refreshSession,
      updateUser,
    }),
    [
      user,
      accessToken,
      loading,
      isAuthenticated,
      isAdmin,
      isLead,
      role,
      permissions,
      can,
      canAny,
      hasAccess,
      isPendingApproval,
      login,
      register,
      logout,
      refreshSession,
      updateUser,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
